<template>
  <div class="datepicker__wrapper" v-if="show" ref="datepickerWrapper" @blur="clickOutside">
    <div
      class="datepicker__dummy-wrapper"
      :class="[
        isOpen ? 'datepicker__dummy-wrapper--is-active' : '',
        showClearSelectionButton ? 'datepicker__dummy-wrapper--has-clear' : ''
      ]"
    >
      <hotel-date-picker-input :i18n="i18n" :input-date="checkIn ? formatDate(checkIn) : undefined" input-date-type="check-in"
        :is-open="isOpen" :show-datepicker="showDatepicker" :hide-datepicker="hideDatepicker"
        :class="checkIn ? 'datepicker__input--is-active' : ''" :toggle-datepicker="toggleDatepicker"
        :single-day-selection="singleDaySelection" />

      <hotel-date-picker-input v-if="!singleDaySelection && (checkIn || checkOut)" :i18n="i18n" :input-date="checkOut ? formatDate(checkOut) : undefined"
        input-date-type="check-out" :is-open="isOpen"
        :class="checkOut ? ['datepicker__input--is-active', 'datepicker__input--dash'] : ''"
        :show-datepicker="showDatepicker" :hide-datepicker="hideDatepicker" :toggle-datepicker="toggleDatepicker"
        :single-day-selection="singleDaySelection" />
    </div>

    <div class="datepicker__clear-button" tabindex="0" @click="clearSelection" v-if="showClearSelectionButton">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 68 68">
        <path d="M6.5 6.5l55 55M61.5 6.5l-55 55" />
      </svg>
    </div>

    <div class="datepicker" :class="isOpen ? 'datepicker--open' : 'datepicker--closed'">
      <div class="datepicker__mobile-header -hide-on-desktop" v-if="isOpen">
        <button type="button" class="datepicker__close-button" @click="hideDatepicker" aria-label="Close">＋</button>
        <div class="datepicker__mobile-title">
          <span v-if="!checkIn && !checkOut">{{ i18n['check-in'] }}</span>
          <template v-else>
            <span>{{ checkIn ? formatDate(checkIn) : i18n['check-in'] }}</span>
            <span v-if="!singleDaySelection && checkOut" class="datepicker__mobile-separator">—</span>
            <span v-if="!singleDaySelection">{{ checkOut ? formatDate(checkOut) : i18n['check-out'] }}</span>
          </template>
        </div>
      </div>

      <div class="datepicker__inner">
        <div class="datepicker__header">
          <span class="datepicker__month-button datepicker__month-button--prev -hide-up-to-tablet"
            @click="renderPreviousMonth" @keyup.enter.stop.prevent="renderPreviousMonth"
            :tabindex="isOpen ? 0 : -1"></span>
          <span class="datepicker__month-button datepicker__month-button--next -hide-up-to-tablet"
            @click="renderNextMonth" @keyup.enter.stop.prevent="renderNextMonth" :tabindex="isOpen ? 0 : -1"></span>
        </div>

        <div class="datepicker__months" v-if="screenSize === 'desktop'">
          <div class="datepicker__month" v-for="n in [0, 1]" :key="n">
            <p class="datepicker__month-name">{{ getMonth(months[activeMonthIndex + n]?.days[15]?.date) }}</p>
            <div class="datepicker__week-row -hide-up-to-tablet">
              <div class="datepicker__week-name" v-for="dayName in i18n['day-names']" :key="dayName">{{ dayName }}</div>
            </div>
            <div class="square" v-for="day in months[activeMonthIndex + n]?.days" :key="day.date.toISOString()" @mouseover="hoveringDate = day.date">
              <hotel-date-picker-day :is-open="isOpen" :options="$props" @day-clicked="handleDayClick($event)"
                :date="day.date" :sorted-disabled-dates="sortedDisabledDates" :next-disabled-date="nextDisabledDate || undefined"
                :active-month-index="activeMonthIndex" :hovering-date="hoveringDate || undefined" :tooltip-message="tooltipMessage"
                :day-number="getDay(day.date)" :belongs-to-this-month="day.belongsToThisMonth" :check-in="checkIn || undefined"
                :check-out="checkOut || undefined" :current-date-style="currentDateStyle" />
            </div>
          </div>
        </div>

        <div class="datepicker__months" id="swiperWrapper" v-else>
          <div class="datepicker__month" v-for="(month, monthIndex) in months" :key="monthIndex">
            <p class="datepicker__month-name">{{ getMonth(month.days[15]?.date) }}</p>
            <div class="datepicker__week-row">
              <div class="datepicker__week-name" v-for="dayName in i18n['day-names']" :key="dayName">{{ dayName }}</div>
            </div>
            <div class="square" v-for="day in month.days" :key="day.date.toISOString()" @mouseover="hoveringDate = day.date">
              <hotel-date-picker-day :is-open="isOpen" :options="$props" @day-clicked="handleDayClick($event)"
                :date="day.date" :sorted-disabled-dates="sortedDisabledDates" :next-disabled-date="nextDisabledDate || undefined"
                :active-month-index="activeMonthIndex" :hovering-date="hoveringDate || undefined" :tooltip-message="tooltipMessage"
                :day-number="getDay(day.date)" :belongs-to-this-month="day.belongsToThisMonth" :check-in="checkIn || undefined"
                :check-out="checkOut || undefined" :current-date-style="currentDateStyle" />
            </div>
          </div>
        </div>
      </div>

      <div class="datepicker__mobile-footer -hide-on-desktop" v-if="isOpen">
        <button
          type="button"
          class="datepicker__confirm-button"
          :disabled="!checkIn || (!singleDaySelection && !checkOut)"
          @click="confirmSelection"
        >
          {{ i18n.submit || 'Забронировать' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import fecha from "fecha"
import lodash from "lodash"
import { onClickOutside } from "@vueuse/core" 

const throttle = lodash.throttle

const datepickerWrapper = ref<HTMLElement | null>(null)

onClickOutside(datepickerWrapper, () => {
  clickOutside()
})

// Props
const props = defineProps({
  currentDateStyle: {
    default: () => ({ border: "1px solid #d9ccaf" })
  },
  modelValue: String,
  startingDateValue: {
    default: null,
    type: Date
  },
  endingDateValue: {
    default: null,
    type: Date
  },
  format: {
    default: "DD.MM.YYYY",
    type: String
  },
  startDate: {
    default: () => new Date(),
    type: [Date, String]
  },
  endDate: {
    default: Infinity,
    type: [Date, String, Number]
  },
  firstDayOfWeek: {
    default: 1,
    type: Number
  },
  minNights: {
    default: 1,
    type: Number
  },
  maxNights: {
    default: null,
    type: Number
  },
  disabledDates: {
    default: () => [],
    type: Array
  },
  disabledDaysOfWeek: {
    default: () => [],
    type: Array
  },
  allowedRanges: {
    default: () => [],
    type: Array
  },
  hoveringTooltip: {
    default: true,
    type: [Boolean, Function]
  },
  tooltipMessage: {
    default: null,
    type: String
  },
  i18n: {
    default: () => ({
      night: "Ночь",
      nights: "Ночей",
      "day-names": ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"],
      "check-in": "Сроки пребывания",
      //  "check-in": "Заезд",
      "check-out": "",
      "submit": "Забронировать",
      "month-names": [
        "Январь",
        "Февраль",
        "Март",
        "Апрель",
        "Май",
        "Июнь",
        "Июль",
        "Август",
        "Сентябрь",
        "Октябрь",
        "Ноябрь",
        "Декабрь"
      ]
    }),
    type: Object
  },
  enableCheckout: Boolean,
  singleDaySelection: Boolean,
  showYear: Boolean,
  closeDatepickerOnClickOutside: {
    default: true,
    type: Boolean
  },
  displayClearButton: {
    default: true,
    type: Boolean
  }
})

const emit = defineEmits(['update:modelValue', 'update:startingDateValue', 'update:endingDateValue', 'confirm'])

const { currentDateStyle, modelValue, startingDateValue, endingDateValue, format, startDate, endDate, firstDayOfWeek, minNights, maxNights, disabledDates, disabledDaysOfWeek, allowedRanges, hoveringTooltip, tooltipMessage, i18n, enableCheckout, singleDaySelection, showYear, closeDatepickerOnClickOutside, displayClearButton } = toRefs(props)

// Store
const store = useOrdersStore()

// State
interface MonthDay {
  date: Date;
  belongsToThisMonth: boolean;
  isInRange: boolean;
}

interface Month {
  days: MonthDay[];
}

// Extend HTMLElement for custom property
interface CustomHTMLElement extends HTMLElement {
  onElementHeightChangeTimer?: ReturnType<typeof setTimeout>;
}

const hoveringDate = ref<Date | null>(null)
const months = ref<Month[]>([])
const activeMonthIndex = ref(0)
const nextDisabledDate = ref<Date | null>(null)
const show = ref(true)
const isOpen = ref(false)
const sortedDisabledDates = ref<Date[]>([])
const screenSize = ref('desktop')

// Touch state
const xDown = ref<number | null>(null)
const yDown = ref<number | null>(null)
const xUp = ref<number | null>(null)
const yUp = ref<number | null>(null)

// Computed
const checkIn = computed(() => store.item.checkIn)
const checkOut = computed(() => store.item.checkOut)

const showClearSelectionButton = computed(() =>
  Boolean((checkIn.value || checkOut.value) && props.displayClearButton)
)

const handleWindowResize = () => {
  if (window.innerWidth < 480) {
    screenSize.value = 'smartphone'
  } else if (window.innerWidth >= 480 && window.innerWidth < 768) {
    screenSize.value = 'tablet'
  } else if (window.innerWidth >= 768) {
    screenSize.value = 'desktop'
  }
}

const formatDate = (date: Date) => {
  if (date) {
    return fecha.format(date, format.value)
  }
  return ""
}

// Watchers
watch(isOpen, (value) => {
  if (screenSize.value !== "desktop") {
    const bodyClassList = document.querySelector("body")?.classList;
    if (value) {
      bodyClassList?.add("-overflow-hidden");
      setTimeout(() => {
        const swiperWrapper = document.getElementById("swiperWrapper");
        const monthElement = document.querySelector(".datepicker__month") as HTMLElement;
        const monthHeight = monthElement?.offsetHeight || 0;
        if (swiperWrapper) {
          swiperWrapper.scrollTop = activeMonthIndex.value * monthHeight;
        }
      }, 100);
    } else {
      bodyClassList?.remove("-overflow-hidden");
    }
  }
})

function parseDisabledDates() {
  const sortedDates: Date[] = [];

  for (let i = 0; i < props.disabledDates.length; i++) {
    sortedDates[i] = new Date(props.disabledDates[i] as string);
  }

  sortedDates.sort((a: Date, b: Date) => a.getTime() - b.getTime());
  sortedDisabledDates.value = sortedDates;
}

// Lifecycle Hooks
onBeforeMount(() => {
  // Initialize fecha i18n and create months
  const fechaI18n = {
    dayNames: props.i18n["day-names"],
    dayNamesShort: shortenString(props.i18n["day-names"], 3),
    monthNames: props.i18n["month-names"],
    monthNamesShort: shortenString(props.i18n["month-names"], 3),
    amPm: ["am", "pm"],
    DoFn: (D: number) => {
      return D + ["th", "st", "nd", "rd"][D % 10 > 3 ? 0 : ((D - (D % 10) !== 10) ? D % 10 : 0)]
    }
  };

  // @ts-ignore - fecha types are incorrect
  fecha.i18n = fechaI18n;

  // Initialize months
  createInitialMonths();
  parseDisabledDates();
})

function getNextDate(datesArray: Date[], referenceDate: Date) {
  var now = new Date(referenceDate);
  var closest: Date | null = null;

  datesArray.forEach(function (d) {
    var date = new Date(d);
    if (date >= now && (closest === null || date < closest)) {
      closest = date;
    }
  });

  return closest;
}

function nextDateByDayOfWeek(weekDay: string, referenceDate: Date) {
  referenceDate = new Date(referenceDate);
  weekDay = weekDay.toLowerCase();
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  let referenceDateDay = referenceDate.getDay();
  let targetDay = 0;

  for (var i = 0; i < 7; i++) {
    if (weekDay === days[i]) {
      targetDay = (i <= referenceDateDay) ? (i + 7) : i;
      break;
    }
  }

  let daysUntilNext = targetDay - referenceDateDay;
  return referenceDate.setDate(referenceDate.getDate() + daysUntilNext);
}

function addDays(date: Date, quantity: number) {
  let result = new Date(date);
  result.setDate(result.getDate() + quantity);
  return result;
}

function getFirstDay(date: Date, firstDayOfWeek: number) {
  var firstDay = getFirstDayOfMonth(date);
  var offset = 0;
  if (firstDayOfWeek > 0) {
    offset = firstDay.getDay() === 0 ? -7 + firstDayOfWeek : firstDayOfWeek;
  }
  return new Date(
    firstDay.setDate(
      firstDay.getDate()
      - (firstDay.getDay() - offset)
    )
  );
}

function getFirstDayOfMonth(date: Date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(), 1
  );
}

function getNextMonth(date: Date) {
  let nextMonth;

  if (date.getMonth() == 11) {
    nextMonth = new Date(date.getFullYear() + 1, 0, 1);
  } else {
    nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);
  }
  return nextMonth;
}

function swipeAfterScroll(direction: string) {
  if (screenSize.value !== 'desktop' && isOpen.value) {
    const swiperWrapper = document.getElementById('swiperWrapper');

    // If wrapper has vertical scroll
    if (swiperWrapper && swiperWrapper.scrollHeight > swiperWrapper.clientHeight) {
      if (swiperWrapper.scrollTop === (swiperWrapper.scrollHeight - swiperWrapper.offsetHeight)) {
        renderNextMonth();
      }
      else if (swiperWrapper.scrollTop === 0) {
        renderPreviousMonth();
      }
      else {
        return;
      }
    }
    else if (direction == 'up') {
      renderNextMonth();
    }
    else if (direction == 'down') {
      renderPreviousMonth();
    }
  }
}

function handleTouchStart(evt: TouchEvent) {
  xDown.value = evt.touches[0].clientX;
  yDown.value = evt.touches[0].clientY;
}

function getMonthDiff(d1: Date, d2: Date) {
  d1 = new Date(d1);
  d2 = new Date(d2);
  const d1Y = d1.getFullYear();
  const d2Y = d2.getFullYear();
  const d1M = d1.getMonth();
  const d2M = d2.getMonth();
  return (d2M + 12 * d2Y) - (d1M + 12 * d1Y);
}

function handleTouchMove(evt: TouchEvent) {
  if (!xDown.value || !yDown.value) {
    return;
  }

  xUp.value = evt.touches[0].clientX;
  yUp.value = evt.touches[0].clientY;

  let xDiff = xDown.value - xUp.value;
  let yDiff = yDown.value - yUp.value;

  if (Math.abs(xDiff) > Math.abs(yDiff)) {
    if (xDiff > 0) {
      // swipe right
    } else {
      // swipe left
    }
  } else {
    if (yDiff > 0) {
      // swipe up
      swipeAfterScroll('up');
    } else {
      // swipe down
      swipeAfterScroll('down');
    }
  }
  xDown.value = null;
  yDown.value = null;
}

function shortenString(arr: string[], sLen: number) {
  let newArr = [];
  for (let i = 0, len = arr.length; i < len; i++) {
    newArr.push(arr[i].substr(0, sLen));
  }
  return newArr;
}

onMounted(() => {
  handleWindowResize()
  document.addEventListener("touchstart", handleTouchStart, false)
  document.addEventListener("touchmove", handleTouchMove, false)
  window.addEventListener("resize", handleWindowResize)
})

onBeforeUnmount(() => {
  document.removeEventListener("touchstart", handleTouchStart)
  document.removeEventListener("touchmove", handleTouchMove)
  window.removeEventListener("resize", handleWindowResize)
})

function createMonth(date: Date) {
  const firstDay = getFirstDay(date, firstDayOfWeek.value);
  let month: Month = {
    days: []
  };

  for (let i = 0; i < 42; i++) {
    const currentDate = addDays(firstDay, i);
    month.days.push({
      date: currentDate,
      belongsToThisMonth: currentDate.getMonth() === date.getMonth(),
      isInRange: false
    });
  }
  months.value.push(month);
}

const createInitialMonths = () => {
  let currentDate = new Date();
  // Desktop shows 2; mobile scrolls a list — seed enough either way
  for (let i = 0; i < 12; i++) {
    createMonth(currentDate);
    currentDate = getNextMonth(currentDate);
  }
}

function onElementHeightChange(el: CustomHTMLElement, callback: () => void) {
  let lastHeight = el.clientHeight;
  let newHeight = lastHeight;

  (function run() {
    newHeight = el.clientHeight;

    if (lastHeight !== newHeight) {
      callback();
    }

    lastHeight = newHeight;

    if (el.onElementHeightChangeTimer) {
      clearTimeout(el.onElementHeightChangeTimer);
    }

    el.onElementHeightChangeTimer = setTimeout(run, 1000);
  })();
}

function emitHeighChangeEvent() {
  emit("update:modelValue", null);
}

function reRender() {
  show.value = false;
  nextTick(() => {
    show.value = true;
  });
}

function clearSelection() {
  hoveringDate.value = null;
  store.setCheckIn(null);
  store.setCheckOut(null);
  nextDisabledDate.value = null;
  show.value = true;
  parseDisabledDates();
  reRender();
}

function hideDatepicker() {
  isOpen.value = false;
}

function showDatepicker() {
  isOpen.value = true;
}

function toggleDatepicker() {
  isOpen.value = !isOpen.value;
}

function confirmSelection() {
  if (!checkIn.value || (!singleDaySelection.value && !checkOut.value)) return;
  hideDatepicker();
  emit('confirm');
}

function clickOutside() {
  if (closeDatepickerOnClickOutside.value) {
    hideDatepicker();
  }
}

function handleDayClick(event: { date: Date; nextDisabledDate: Date | null }) {
  if (checkIn.value == null && singleDaySelection.value == false) {
    store.setCheckIn(event.date);
  } else if (singleDaySelection.value == true) {
    store.setCheckIn(event.date);
    store.setCheckOut(event.date);
  } else if (checkIn.value !== null && checkOut.value == null) {
    store.setCheckOut(event.date);
  } else {
    store.setCheckOut(null);
    store.setCheckIn(event.date);
  }
  nextDisabledDate.value = event.nextDisabledDate;
}

function renderPreviousMonth() {
  if (activeMonthIndex.value >= 1) {
    activeMonthIndex.value--;
  } else return;
}

const renderNextMonth = throttle(function throttleRenderNextMonth() {
  if (activeMonthIndex.value < months.value.length - 2) {
    activeMonthIndex.value++;
    return;
  }

  let firstDayOfLastMonth;
  if (screenSize.value !== "desktop") {
    firstDayOfLastMonth = months.value[months.value.length - 1].days.filter(
      (day: MonthDay) => day.belongsToThisMonth === true
    );
  } else {
    firstDayOfLastMonth = months.value[
      activeMonthIndex.value + 1
    ].days.filter((day: MonthDay) => day.belongsToThisMonth === true);
  }

  if (endDate.value !== Infinity) {
    if (
      fecha.format(firstDayOfLastMonth[0].date, "YYYYMM") ==
      fecha.format(new Date(endDate.value), "YYYYMM")
    ) {
      return;
    }
  }

  createMonth(getNextMonth(firstDayOfLastMonth[0].date));
  activeMonthIndex.value++;
}, 200)

function getDay(date: Date) {
  return fecha.format(date, "D");
}

function getMonth(date?: Date) {
  if (!date || isNaN(date.getTime())) return "";
  try {
    const monthIndex = date.getMonth();
    const yearSuffix = showYear.value ? ` ${date.getFullYear()}` : "";
    return i18n.value["month-names"][monthIndex] + yearSuffix;
  } catch (error) {
    console.error(error);
    return "";
  }
}
</script>

<style lang="scss">
/* =============================================================
     * VARIABLES
     * ============================================================*/
$white: #fff;
$black: #000;
$gray: #424b53;
$primary-text-color: #4a3e3e;
$lightest-gray: #f3f5f8;
$primary-color: #4a3e3e;
$medium-gray: #999999;
$light-gray: #707070;
$dark-gray: #2d3047;

$font-small: 14px;

/* =============================================================
     * RESPONSIVE LAYOUT HELPERS
     * ============================================================*/
$tablet: "(min-width: 480px) and (max-width: 767px)";
$phone: "(max-width: 479px)";
$desktop: "(min-width: 768px)";
$up-to-tablet: "(max-width: 767px)";
$extra-small-screen: "(max-width: 23em)";

@mixin focusStyle() {
  &:focus {
    outline: 1px dashed darken($primary-color, 10%);
    outline-offset: -10px;
  }
}

@mixin device($device-widths) {
  @media screen and #{$device-widths} {
    @content;
  }
}

.square {
  width: calc(100% / 7);
  float: left;

  @include device($desktop) {
    cursor: pointer;
  }
}

.datepicker__wrapper {
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
}

/* =============================================================
     * BASE STYLES
     * ============================================================*/
.oou {
  color: blue
}

.datepicker {
  transition: all 0.2s ease-in-out;
  background-color: $white;
  color: $gray;

  font-family: "Lato" !important;
  font-size: 16px !important;
  line-height: 14px;
  overflow: hidden;
  left: 0;
  top: 48px;
  position: absolute;
  z-index: 999;

  button.next--mobile {
    background: none;
    border: 1px solid $light-gray;
    float: none;
    height: 50px;
    width: 100%;
    position: relative;
    background-position: center;
    appearance: none;
    overflow: hidden;
    position: fixed;
    bottom: 0;
    left: 0;
    outline: none;
    box-shadow: 0 5px 30px 10px rgba($black, 0.08);
    background: white;

    &:after {
      background: transparent url("/ic-arrow-right-datepicker.regular.svg") no-repeat center / 8px;
      transform: rotate(90deg);
      content: "";
      position: absolute;
      width: 200%;
      height: 200%;
      top: -50%;
      left: -50%;
    }
  }

  &--closed {
    box-shadow: 0 15px 30px 10px rgba($black, 0);
    max-height: 0;
  }

  &--open {
    box-shadow: 0 15px 30px 10px rgba($black, 0.08);
    max-height: 900px;

    @include device($up-to-tablet) {
      box-shadow: none;
      height: 100%;
      left: 0;
      right: 0;
      bottom: 0;
      -webkit-overflow-scrolling: touch !important;
      position: fixed;
      top: 0;
      width: 100%;
    }
  }

  &__wrapper {
    position: relative;
    display: block;
    width: 100%;
    min-height: 44px;
    height: auto;
    // background: $white url("/calendar_icon.regular.svg") no-repeat 17px center /
    //   16px;
  }

  &__input {
    background: transparent;
    height: 44px;
    color: $primary-text-color;
    font-size: 16px;
    outline: none;
    // padding: 4px 30px 2px;
    width: 100%;
    word-spacing: 5px;
    border: 0;

    @include focusStyle();

    &::-webkit-input-placeholder,
    &::-moz-placeholder,
    &:-ms-input-placeholder,
    &:-moz-placeholder {
      color: $primary-text-color;
    }
  }

  &__dummy-wrapper {
    // border: solid 1px $light-gray;
    border-bottom: 1px solid #707070 !important;
    font-family: "Lato";
    font-size: 16px;
    outline: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    float: none;
    width: 100%;
    min-height: 44px;
    height: auto;
    box-sizing: border-box;
    padding-right: 0;

    &--no-border.datepicker__dummy-wrapper {
      margin-top: 15px;
      border: 0;
    }

    &--is-active {
      border: 1px solid $primary-color;
    }

    &--has-clear {
      padding-right: 40px;
    }
  }


  &__input {
    color: $primary-text-color;
    padding-top: 0;
    font-size: 16px;
    color: #8D8D8D;
    outline: none !important;
    float: none;
    height: auto;
    min-height: 44px;
    line-height: 44px;
    text-align: left;
    //  text-indent: 5px;
    width: auto;
    flex: 0 0 auto;
    white-space: nowrap;

    @include device($phone) {
      text-indent: 0;
      text-align: left;
    }

    &:first-child {
      // background: transparent url("/ic-arrow-right-datepicker.regular.svg")
      //   no-repeat right center / 8px;
      flex: 0 1 auto;
      min-width: 0;
      width: auto;
      max-width: 100%;
      word-spacing: 1px;
      // text-indent: 20px;
    }

    &--dash:before {
      content: '—';
      padding-right: 10px;
      padding-left: 10px;
    }

    &--is-active {
      color: $primary-color;

    }

    &--is-active::placeholder {
      color: $primary-color;
    }

    &--is-active::-moz-placeholder {
      color: $primary-color;
    }

    &--is-active:-ms-input-placeholder {
      color: $primary-color;
    }

    &--is-active:-moz-placeholder {
      color: $primary-color;
    }

    &--single-date:first-child {
      width: 100%;
      background: none;
      text-align: left;
    }
  }

  &__month-day {
    visibility: visible;
    text-align: center;
    margin: 0;
    border: 0;
    height: 40px;
    padding-top: 14px;

    @include focusStyle();

    &--invalid-range {
      background-color: rgba($primary-color, 0.3);
      color: $lightest-gray;
      cursor: not-allowed;
      position: relative;
    }

    &--invalid {
      color: $lightest-gray;
      cursor: not-allowed;
    }

    &--valid:hover,
    &--allowed-checkout:hover {
      background-color: $white;
      color: $primary-color;
      z-index: 1;
      position: relative;
      box-shadow: 0 0 10px 3px rgba($gray, 0.4);
    }

    &--disabled {
      opacity: 0.25;
      cursor: not-allowed;
      pointer-events: none;
      position: relative;
    }

    &--selected {
      background-color: rgba($primary-color, 0.5);
      color: $white;

      &:hover {
        background-color: $white;
        color: $primary-color;
        z-index: 1;
        position: relative;
        box-shadow: 0 0 10px 3px rgba($gray, 0.4);
      }
    }

    &--first-day-selected,
    &--last-day-selected {
      background: $primary-color;
      color: $white;
    }

    &--allowed-checkout {
      color: $medium-gray;
    }

    &--out-of-range {
      color: $lightest-gray;
      cursor: not-allowed;
      position: relative;
      pointer-events: none;
    }

    &--valid {
      cursor: pointer;
      color: $medium-gray;
    }

    &--hidden {
      opacity: 0.25;
      pointer-events: none;
      color: $white;
    }
  }

  &__month-button {
    background: transparent url("/ic-arrow-right-datepicker.regular.svg") no-repeat center center / 8px;
    cursor: pointer;
    display: inline-block;
    height: 60px;
    width: 60px;

    @include focusStyle();

    &--prev {
      transform: rotateY(180deg);
    }

    &--next {
      float: right;
    }

    &--locked {
      opacity: 0.2;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  &__inner {
    padding: 20px;
    float: left;

    @include device($up-to-tablet) {
      padding: 0;
    }
  }

  &__months {
    @include device($desktop) {
      width: 650px;
    }

    @include device($up-to-tablet) {
      margin-top: 0;
      height: calc(100% - 57px - 76px);
      position: absolute;
      left: 0;
      top: 57px;
      overflow: scroll;
      right: 0;
      bottom: 76px;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      padding: 0 12px 24px;
    }

    &::before {
      background: $light-gray;
      bottom: 0;
      content: "";
      display: block;
      left: 50%;
      position: absolute;
      top: 0;
      width: 1px;

      @include device($up-to-tablet) {
        display: none;
      }
    }
  }

  &__month {
    font-size: 12px;
    float: left;
    width: 50%;
    padding-right: 10px;

    @include device($up-to-tablet) {
      width: 100%;
      padding-right: 0;
      padding-top: 24px;
      position: relative;

      &:last-of-type {
        margin-bottom: 65px;
      }
    }

    @include device($desktop) {
      &:last-of-type {
        padding-right: 0;
        padding-left: 10px;
      }
    }
  }

  &__month-caption {
    height: 2.5em;
    vertical-align: middle;
  }

  &__month-name {
    font-size: 16px;
    font-weight: 500;
    margin-top: -40px;
    padding-bottom: 17px;
    pointer-events: none;
    text-align: center;

    @include device($up-to-tablet) {
      margin-top: 0;
      margin-bottom: 12px;
      position: relative;
      width: 100%;
    }
  }

  &__week-days {
    height: 2em;
    vertical-align: middle;
  }

  &__week-row {
    border-bottom: 5px solid $white;
    height: 38px;

    @include device($up-to-tablet) {
      box-shadow: none;
      height: 25px;
      left: auto;
      top: auto;
      position: relative;
      width: 100%;
      margin-bottom: 8px;
    }
  }

  &__week-name {
    width: calc(100% / 7);
    float: left;
    font-size: 12px;
    font-weight: 400;
    color: $medium-gray;
    text-align: center;
  }

  &__mobile-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid #e5e5e5;
    position: relative;
    z-index: 2;
  }

  &__mobile-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: "Lato", sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #333;
    line-height: 1.2;
  }

  &__mobile-footer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 3;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    background: #fff;
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.06);
  }

  &__confirm-button {
    appearance: none;
    border: none;
    width: 100%;
    height: 50px;
    background-color: #d9ccaf;
    color: #000;
    font-family: "Lato", sans-serif;
    font-size: 16px;
    cursor: pointer;

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__close-button {
    appearance: none;
    background: transparent;
    border: 0;
    color: $primary-color;
    cursor: pointer;
    font-size: 28px;
    font-weight: bold;
    margin: 0;
    padding: 0;
    outline: 0;
    line-height: 1;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: rotate(45deg);
  }

  &__clear-button {
    appearance: none;
    background: transparent;
    border: 0;
    cursor: pointer;
    font-size: 25px;
    font-weight: bold;
    height: 44px;
    margin: 0;
    padding: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      fill: none;
      stroke-linecap: round;
      stroke-width: 8px;
      stroke: $medium-gray;
      width: 14px;
      position: relative;
    }

    @include focusStyle();
  }

  &__tooltip {
    background-color: $dark-gray;
    border-radius: 2px;
    color: $white;
    font-size: 11px;
    margin-left: 5px;
    margin-top: -22px;
    padding: 5px 10px;
    position: absolute;
    z-index: 50;

    &:after {
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 4px solid $dark-gray;
      bottom: -4px;
      content: "";
      left: 50%;
      margin-left: -4px;
      position: absolute;
    }
  }
}

// Modifiers

.-overflow-hidden {
  overflow: hidden;
}

.-is-hidden {
  display: none;
}

.-hide-up-to-tablet {
  @include device($up-to-tablet) {
    display: none;
  }
}

.-hide-on-desktop {
  @include device($desktop) {
    display: none;
  }
}
</style>
